import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { AiOutlineOpenAI } from "react-icons/ai";
import { useLanguage } from "../i18n";
import SupportReply from "./SupportReply";
import "./WelcomeAssistant.css";

const FIRST_WEEK_MS = 7 * 24 * 60 * 60 * 1000;

const copy = {
  en: { button: "Questions? I’m here to help.", attribution: "AI by OpenAI", greeting: "Hi, I’m Bubly Support. I can help you get started.", firstPost: "How do I publish my first photo?", profileHelp: "What can I do on my profile?", placeholder: "Ask Bubly Support…", send: "Send", sending: "Thinking…", unavailable: "I’m having trouble connecting right now. You can find me on the Support page.", close: "Close Bubly Support" },
  es: { button: "¿Preguntas? Estoy aquí para ayudarte.", attribution: "IA de OpenAI", greeting: "Hola, soy Bubly Support. Puedo ayudarte a empezar.", firstPost: "¿Cómo publico mi primera foto?", profileHelp: "¿Qué puedo hacer en mi perfil?", placeholder: "Pregunta a Bubly Support…", send: "Enviar", sending: "Pensando…", unavailable: "Ahora mismo tengo problemas para conectarme. Puedes encontrarme en la página de ayuda.", close: "Cerrar Bubly Support" },
  fr: { button: "Une question ? Je suis là pour vous aider.", attribution: "IA par OpenAI", greeting: "Bonjour, je suis Bubly Support. Je peux vous aider à démarrer.", firstPost: "Comment publier ma première photo ?", profileHelp: "Que puis-je faire sur mon profil ?", placeholder: "Posez votre question…", send: "Envoyer", sending: "Réponse en cours…", unavailable: "Je n’arrive pas à me connecter pour le moment. Retrouvez-moi sur la page Assistance.", close: "Fermer Bubly Support" },
  pt: { button: "Dúvidas? Estou aqui para ajudar.", attribution: "IA da OpenAI", greeting: "Olá, sou o Bubly Support. Posso ajudar você a começar.", firstPost: "Como publico minha primeira foto?", profileHelp: "O que posso fazer no meu perfil?", placeholder: "Pergunte ao Bubly Support…", send: "Enviar", sending: "Pensando…", unavailable: "Estou com dificuldade para conectar agora. Você pode me encontrar na página de suporte.", close: "Fechar Bubly Support" },
  et: { button: "Küsimusi? Olen siin, et aidata.", attribution: "OpenAI tehisintellekt", greeting: "Tere! Olen Bubly Support. Aitan sul alustada.", firstPost: "Kuidas avaldada oma esimene foto?", profileHelp: "Mida saan oma profiilil teha?", placeholder: "Küsi Bubly Supportilt…", send: "Saada", sending: "Mõtlen…", unavailable: "Praegu ei õnnestu ühendust luua. Leiad mind toe lehelt.", close: "Sulge Bubly Support" },
  no: { button: "Spørsmål? Jeg er her for å hjelpe.", attribution: "AI fra OpenAI", greeting: "Hei, jeg er Bubly Support. Jeg kan hjelpe deg i gang.", firstPost: "Hvordan publiserer jeg mitt første bilde?", profileHelp: "Hva kan jeg gjøre på profilen min?", placeholder: "Spør Bubly Support…", send: "Send", sending: "Tenker…", unavailable: "Jeg har problemer med å koble til akkurat nå. Du finner meg på supportsiden.", close: "Lukk Bubly Support" },
  de: { button: "Fragen? Ich helfe dir gerne.", attribution: "KI von OpenAI", greeting: "Hallo, ich bin Bubly Support. Ich helfe dir beim Einstieg.", firstPost: "Wie veröffentliche ich mein erstes Foto?", profileHelp: "Was kann ich in meinem Profil tun?", placeholder: "Frag Bubly Support…", send: "Senden", sending: "Denke nach…", unavailable: "Ich kann gerade keine Verbindung herstellen. Du findest mich auf der Supportseite.", close: "Bubly Support schließen" },
  zh: { button: "有问题？我来帮你。", attribution: "OpenAI AI 助手", greeting: "你好，我是 Bubly Support，可以帮你开始使用。", firstPost: "如何发布我的第一张照片？", profileHelp: "我可以在个人资料中做什么？", placeholder: "向 Bubly Support 提问…", send: "发送", sending: "思考中…", unavailable: "我现在暂时无法连接。你可以在支持页面找到我。", close: "关闭 Bubly Support" },
  ja: { button: "ご質問ですか？お手伝いします。", attribution: "OpenAIのAIアシスタント", greeting: "こんにちは、Bubly Supportです。使い始めるお手伝いをします。", firstPost: "最初の写真を投稿するには？", profileHelp: "プロフィールでは何ができますか？", placeholder: "Bubly Supportに質問…", send: "送信", sending: "考え中…", unavailable: "現在接続できません。サポートページからご利用ください。", close: "Bubly Supportを閉じる" }
};

export default function WelcomeAssistant({ user }) {
  const { language } = useLanguage();
  const location = useLocation();
  const text = copy[language] || copy.en;
  const [now, setNow] = useState(Date.now());
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [unavailable, setUnavailable] = useState(false);

  const parsedCreatedAt = Date.parse(user?.createdAt || "");
  const createdAt = Number.isFinite(parsedCreatedAt) ? parsedCreatedAt : null;

  useEffect(() => {
    if (createdAt === null) return undefined;
    const remaining = FIRST_WEEK_MS - (Date.now() - createdAt);
    if (remaining <= 0) {
      setNow(Date.now());
      return undefined;
    }
    const timer = window.setTimeout(() => setNow(Date.now()), remaining);
    return () => window.clearTimeout(timer);
  }, [createdAt]);

  const age = createdAt === null ? Number.POSITIVE_INFINITY : now - createdAt;
  const eligiblePage = location.pathname === "/" || location.pathname === "/my-profile";
  if (!eligiblePage || age < 0 || age >= FIRST_WEEK_MS) return null;

  const close = () => setOpen(false);
  const sendMessage = async (event, suggestedText = "") => {
    event?.preventDefault();
    const message = (suggestedText || prompt).trim();
    if (!message || loading || message.length > 2000) return;

    const nextMessages = [...(messages.length ? messages : [{ role: "assistant", content: text.greeting }]), { role: "user", content: message }];
    setMessages(nextMessages);
    setPrompt("");
    setLoading(true);
    setUnavailable(false);
    try {
      const response = await fetch("/api/support/chat", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ category: "account", language, messages: nextMessages.slice(-9) })
      });
      const data = await response.json();
      if (!response.ok || !data.ok || typeof data.reply !== "string") throw new Error("welcome_assistant_unavailable");
      setMessages((current) => [...current, { role: "assistant", content: data.reply }]);
    } catch (_error) {
      setUnavailable(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <aside className="welcome-assistant" aria-label="Bubly Support">
      {open && <section className="welcome-assistant-panel" aria-label="Bubly Support chat">
        <header className="welcome-assistant-header">
          <span className="welcome-assistant-openai-mark" aria-hidden="true"><AiOutlineOpenAI /></span>
          <div><strong>Bubly Support</strong><small>{text.attribution}</small></div>
          <button type="button" onClick={close} aria-label={text.close}>×</button>
        </header>
        <div className="welcome-assistant-messages" role="log" aria-live="polite">
          {(messages.length ? messages : [{ role: "assistant", content: text.greeting }]).map((item, index) => (
            <div className={`welcome-assistant-message ${item.role}`} key={`${item.role}-${index}`}>
              {item.role === "assistant" ? <SupportReply text={item.content} onNavigate={close} /> : item.content}
            </div>
          ))}
          {messages.length <= 1 && <div className="welcome-assistant-suggestions">
            <button type="button" onClick={(event) => sendMessage(event, text.firstPost)}>{text.firstPost}</button>
            <button type="button" onClick={(event) => sendMessage(event, text.profileHelp)}>{text.profileHelp}</button>
          </div>}
          {loading && <div className="welcome-assistant-message assistant">{text.sending}</div>}
          {unavailable && <p className="welcome-assistant-error" role="alert">{text.unavailable}</p>}
        </div>
        <form className="welcome-assistant-form" onSubmit={sendMessage}>
          <input value={prompt} onChange={(event) => setPrompt(event.target.value)} maxLength={2000} placeholder={text.placeholder} aria-label={text.placeholder} />
          <button type="submit" disabled={!prompt.trim() || loading}>{loading ? text.sending : text.send}</button>
        </form>
      </section>}
      <button type="button" className={`welcome-assistant-trigger ${open ? "is-open" : ""}`} onClick={() => setOpen((value) => !value)} aria-expanded={open}>
        <span className="welcome-assistant-openai-mark" aria-hidden="true"><AiOutlineOpenAI /></span>
        <span className="welcome-assistant-trigger-copy"><strong>Bubly Support</strong><small>{text.button}</small></span>
        {!open && <i aria-hidden="true" />}
      </button>
    </aside>
  );
}
